<template>
  <section>
    <one-line-form v-if="!isAutoFillEnabled" :submit="updateHouseNumber" :submittable="isValid" :saving="isSaving ? houseNumber : ''" :loading="willLookupPostalCode">
      <input-field name="house"
                   id="tel"
                   v-show="app.keyboardType === 'tel'"
                   :class="{ invisible: isSaving }"
                   type="text"
                   :title="i18n.t('house:house_number')"
                   :placeholder="i18n.t('house:enter_house_number')"
                   :value="houseNumber"
                  :pattern="pattern"
                   @input="syncHouseNumber"
                   patternPlaceholder=" "
                   v-keyboard="'tel'"
                   autocomplete="cc-csc" />
      <input-field name="house2"
                   id="alphanumeric"
                   v-show="app.keyboardType === 'alphanumeric'"
                   :class="{ invisible: isSaving }"
                   type="text"
                   :title="i18n.t('house:house_number')"
                   :placeholder="i18n.t('house:enter_house_number')"
                   :value="houseNumber"
                   @input="syncHouseNumber"
                   patternPlaceholder=" "
                   v-keyboard="'alphanumeric'"
                   autocomplete="cc-csc" />
    </one-line-form>
    <house-number-autofill v-if="isAutoFillEnabled" :screen-params="screenParams">
      <helper>
        {{ entity.streetName }}, {{ entity.postalCode === user.GeoPostalCode || entity.postalCode === app.unknownPostcode ? '' : entity.postalCode}} {{ entity.city }}
      </helper>
    </house-number-autofill>
    <helper v-if="entity.country === 'US' && !isAutoFillEnabled">
      {{ entity.streetName }}, {{ entity.city }}, {{ entity.province }}, {{ entity.postalCode === user.GeoPostalCode || entity.postalCode === app.unknownPostcode ? '' : entity.postalCode}}
    </helper>
    <helper v-else-if="!isAutoFillEnabled" :class="{ invisible : isSaving, keyboardOn: app.keyboardHeight}">
      {{ entity.streetName }}, {{ entity.postalCode === user.GeoPostalCode || entity.postalCode === app.unknownPostcode || (this.app.country.postalCodePattern && entity.postalCode === '0'.repeat(this.app.country.postalCodePattern.length))? '' : entity.postalCode}} {{ entity.city }}
    </helper>
    <additional-keyboard v-if="app.keyboardType === 'tel' && !isTabletOrBigger && !isAutoFillEnabled">
      <char-keyboard chars="A,B,C,···" :directInput="true"/>
    </additional-keyboard>
    <show-keyboard v-if="!isAutoFillEnabled"></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';
  .keyboardOn {
    bottom: 15px;
  }
</style>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'

  import { setSelection } from '../utils/selection'
  import CharKeyboard from '../components/CharKeyboard'
  import BottomHints from '../components/BottomHints'
  import Breakpoints from '../mixins/breakpoints'
  import AdditionalKeyboard from '../components/AdditionalKeyboard'
  import HouseNumberAutofill from '../components/HouseNumberAutofill'
  import { getNestedProperty, areConditionsPassed } from '../utils/structure.js'
  import fieldCustomRules from '../mixins/customFieldsRules'

  export default Vue.component('personal-house-number-screen', {
    components: {
      'char-keyboard': CharKeyboard,
      'bottom-hints': BottomHints,
      'additional-keyboard': AdditionalKeyboard,
      'house-number-autofill': HouseNumberAutofill
    },
    props: ['screenParams'],
    mixins: [
      Breakpoints,
      fieldCustomRules
    ],
    data () {
      const postalCodePrediction = this.$store.state.postalCodePrediction
      const companyPrediction = this.$store.state.companyPrediction && this.$store.state.companyPrediction.matchCompany
      const partner = this.$store.state.partner
      const app = this.$store.state.app

      const dispatchKeyConditions = this.screenParams && this.screenParams.dispatchKey && this.screenParams.dispatchKey.conditions
      const dispatchKeyString = this.screenParams && this.screenParams.dispatchKey && typeof this.screenParams.dispatchKey === 'string' && this.screenParams.dispatchKey
      const dispatchKey = (dispatchKeyConditions && areConditionsPassed(this.$store, this.screenParams.dispatchKey.conditions) && this.screenParams.dispatchKey.prop) || dispatchKeyString || 'updateUser'
      const key = dispatchKey.replace('update', '').toLowerCase()
      const propertyName = (this.screenParams && this.screenParams.propertyName) || 'houseNumber'

      let propertyEntity = propertyName.split('.')
      propertyEntity = propertyEntity.length > 1 ? propertyEntity.slice(0, -1).join('.') : false
      const entityData = propertyEntity ? getNestedProperty(this.$store.state[key], propertyEntity) : this.$store.state[key]

      const userHouseNumberPrediction = postalCodePrediction.enabled && postalCodePrediction.matchPerson.houseNumber
      const companyHouseNumberPrediction = companyPrediction && companyPrediction.houseNumber
      const allowedCountriesForReverseLookup = ['US', 'DE', 'SE', 'GB', 'NO', 'FI', 'DK', 'SG', 'CA', 'RU', 'UA', 'TR', 'ID', 'MY', 'KZ', 'IL']
      const canDoPersonalDataLookup = this.$store.getters.canDoPersonalDataLookup

      const isOnelineaddressReverseLookup = !app.isManualAddreessFlow && app.country.code && partner.oneInputAddressCountries && partner.oneInputAddressCountries.includes(app.country.code)
      const isStandardaddressReverseLookup = !!(canDoPersonalDataLookup && (!entityData.postalCode || entityData.postalCode === app.unknownPostcode ) && allowedCountriesForReverseLookup.includes(app.country.code))
      const willLookupPostalCode = !!isOnelineaddressReverseLookup || isStandardaddressReverseLookup

      return {
        houseNumber: entityData.houseNumber || (key === 'user' && userHouseNumberPrediction) || (key === 'company' && companyHouseNumberPrediction) || '',
        isSaving: false,
        entity: entityData,
        propertyName: propertyName,
        dispatchKey: dispatchKey || 'updateUser',
        willLookupPostalCode
      }
    },
    computed: {
      ...mapState(['app', 'user', 'phonePrevalidation', 'postalArea']),
      isAutoFillEnabled () {
        return this.user.country === 'DK'
      },
      isValid () {
        return this.isValidLength(this.houseNumber,'houseNumber')
      },
      pattern () {
        const customFieldRuless = this.getFieldCustomRules("houseNumber")
        const maxLength = customFieldRuless && customFieldRuless.maxLength
        return maxLength ? '_'.repeat(maxLength) : null
      }
    },
    watch: {
      'app.keyboardType' (newVal, oldVal) {
        this.toggleKeyboard(newVal)
      },
      isValid () {
        if (this.isValid) {
          this.$emit('tooltip', ['enter'])
        } else {
          this.$emit('tooltip', ['type'])
        }
      },
      isSaving () {
        if (this.isSaving) {
          this.$emit('tooltip', 'hide')
        }
      }
    },
    created () {
      this.$store.dispatch('updateApp', {
        keyboardType: this.app.deviceIsIOS ? 'alphanumeric' : 'tel'
      })
    },
    methods: {
      syncHouseNumber (value) {
        if (this.usingPrefill && value !== this.houseNumber) this.usingPrefill = false
        this.houseNumber = value
      },
      async updateHouseNumber () {
        if (this.houseNumber && !this.willLookupPostalCode) {
          this.isSaving = true
          let dispatchData = {}
          dispatchData[this.propertyName] = this.houseNumber

          this.$store.dispatch(this.dispatchKey, dispatchData)
          this.trackPrefill('houseNumber', this.houseNumber)

          return this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        } else if (this.houseNumber) {
          this.isSaving = true
          let dispatchData = {}

          await this.$store.dispatch('lookupPostalCode', { province: this.entity.province, city: this.entity.city, streetName: this.entity.streetName, houseNumber: this.houseNumber ? this.houseNumber.replace(/ /g, '') : '' })

          if (!this.postalArea.streets || this.postalArea.streets.length === 0) {
            this.willLookupPostalCode = false
            return this.updateHouseNumber()
          } else {
            let street = this.postalArea.streets[0]
            const property = this.propertyName.split('.')
            const propertyPostalcode = property.length > 1 ? property.slice(0, -1).join('.') + '.postalCode' : 'postalCode'
            const propertyProvince = property.length > 1 ? property.slice(0, -1).join('.') + '.province' : 'province'

            dispatchData[propertyPostalcode] = street.postalCode
            if (street.province) {
              dispatchData[propertyProvince] = street.province
            }
          }

          dispatchData[this.propertyName] = this.houseNumber

          this.$store.dispatch(this.dispatchKey, dispatchData)
          this.willLookupPostalCode = false
          this.trackPrefill('houseNumber', this.houseNumber)

          return this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }
      },
      toggleKeyboard (type) {
        this.$nextTick(() => this.focus(type))
      },
      focus (type) {
        let input = this.$el.querySelector(`#${type} input`)
        const length = this.houseNumber ? this.houseNumber.length : 0
        if (input) {
          setSelection(input, { start: length, end: length })
        }
      }
    },
    mounted () {
      this.$emit('tooltip', ['type'])
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/PersonalHouseNumber.vue